import { ReactNode, ButtonHTMLAttributes, InputHTMLAttributes, useState } from 'react';
import { useImpersonation } from '@shared/contexts/ImpersonationContext';

// Type references for DOM elements
type HTMLButtonElement = globalThis.HTMLButtonElement;
type HTMLInputElement = globalThis.HTMLInputElement;

interface ImpersonationGuardProps {
  children: ReactNode;
  disabledTooltip?: string;
  fallback?: ReactNode;
}

/**
 * Wrapper component that disables children during impersonation.
 * Shows a tooltip explaining why the action is disabled.
 *
 * Usage with buttons:
 * <ImpersonationGuard>
 *   <button onClick={handleSensitiveAction}>Delete Account</button>
 * </ImpersonationGuard>
 *
 * Usage with forms:
 * <ImpersonationGuard fallback={<div>Form disabled during impersonation</div>}>
 *   <form>...</form>
 * </ImpersonationGuard>
 */
export function ImpersonationGuard({
  children,
  disabledTooltip = 'Not available during impersonation',
  fallback,
}: ImpersonationGuardProps) {
  const { isImpersonating } = useImpersonation();
  const [showTooltip, setShowTooltip] = useState(false);

  if (!isImpersonating) {
    return <>{children}</>;
  }

  // If custom fallback is provided, use it
  if (fallback) {
    return <>{fallback}</>;
  }

  // Otherwise, wrap children with disabled state and tooltip
  return (
    <div
      className="inline-block relative"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      onFocus={() => setShowTooltip(true)}
      onBlur={() => setShowTooltip(false)}
    >
      <div
        className="opacity-50 pointer-events-none select-none"
        style={{ filter: 'grayscale(100%)' }}
      >
        {children}
      </div>

      {showTooltip && (
        <div className="absolute z-50 px-3 py-2 text-sm font-medium text-white bg-slate-900 rounded-lg shadow-lg bottom-full left-1/2 transform -translate-x-1/2 mb-2 whitespace-nowrap">
          {disabledTooltip}
          <div className="absolute w-2 h-2 bg-slate-900 transform rotate-45 -bottom-1 left-1/2 -translate-x-1/2" />
        </div>
      )}
    </div>
  );
}

/**
 * HOC version for wrapping button components
 */
export function withImpersonationGuard<P extends ButtonHTMLAttributes<HTMLButtonElement>>(
  WrappedComponent: React.ComponentType<P>,
  disabledTooltip = 'Not available during impersonation'
) {
  return function GuardedComponent(props: P) {
    const { isImpersonating } = useImpersonation();
    const [showTooltip, setShowTooltip] = useState(false);

    const guardedProps = {
      ...props,
      disabled: props.disabled || isImpersonating,
      className: `${props.className || ''} ${isImpersonating ? 'opacity-50 cursor-not-allowed' : ''}`,
      ...(isImpersonating && {
        onMouseEnter: () => setShowTooltip(true),
        onMouseLeave: () => setShowTooltip(false),
        onFocus: () => setShowTooltip(true),
        onBlur: () => setShowTooltip(false),
      }),
    };

    return (
      <div className="inline-block relative">
        <WrappedComponent {...guardedProps} />
        {isImpersonating && showTooltip && (
          <div className="absolute z-50 px-3 py-2 text-sm font-medium text-white bg-slate-900 rounded-lg shadow-lg bottom-full left-1/2 transform -translate-x-1/2 mb-2 whitespace-nowrap">
            {disabledTooltip}
            <div className="absolute w-2 h-2 bg-slate-900 transform rotate-45 -bottom-1 left-1/2 -translate-x-1/2" />
          </div>
        )}
      </div>
    );
  };
}

/**
 * HOC version for wrapping input components
 */
export function withImpersonationInputGuard<P extends InputHTMLAttributes<HTMLInputElement>>(
  WrappedComponent: React.ComponentType<P>,
  disabledTooltip = 'Not available during impersonation'
) {
  return function GuardedComponent(props: P) {
    const { isImpersonating } = useImpersonation();
    const [showTooltip, setShowTooltip] = useState(false);

    const guardedProps = {
      ...props,
      disabled: props.disabled || isImpersonating,
      readOnly: isImpersonating,
      className: `${props.className || ''} ${isImpersonating ? 'opacity-50 cursor-not-allowed bg-surface-2-light dark:bg-surface-2-dark' : ''}`,
      ...(isImpersonating && {
        onMouseEnter: () => setShowTooltip(true),
        onMouseLeave: () => setShowTooltip(false),
        onFocus: () => setShowTooltip(true),
        onBlur: () => setShowTooltip(false),
      }),
    };

    return (
      <div className="inline-block relative">
        <WrappedComponent {...guardedProps} />
        {isImpersonating && showTooltip && (
          <div className="absolute z-50 px-3 py-2 text-sm font-medium text-white bg-slate-900 rounded-lg shadow-lg bottom-full left-1/2 transform -translate-x-1/2 mb-2 whitespace-nowrap">
            {disabledTooltip}
            <div className="absolute w-2 h-2 bg-slate-900 transform rotate-45 -bottom-1 left-1/2 -translate-x-1/2" />
          </div>
        )}
      </div>
    );
  };
}

/**
 * Hook for checking if an action should be disabled during impersonation
 */
export function useImpersonationGuard() {
  const { isImpersonating } = useImpersonation();

  return {
    isImpersonating,
    shouldDisable: isImpersonating,
    disabledTooltip: 'Not available during impersonation',
  };
}
